import React, { useMemo, useState } from "react";
import { FONT, BLUE, BLUE_MED, TEXT, TEXT2, TEXT3, BORDER, SUCCESS, DANGER } from "../shared";
import VellaAreaChart from "./ui/VellaAreaChart";

// ─────────────────────────────────────────────────────────────────────────
// WebsiteAnalytics — website traffic dashboard for the "My Website" page.
// Frosted-glass cards over the ocean-blue Vella Design System. Lightweight,
// dependency-free SVG area chart. Mock data for now; the backend analytics
// API can be wired in later by replacing the constants below.
// ─────────────────────────────────────────────────────────────────────────

const glassCard = {
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(40px) saturate(180%)",
  WebkitBackdropFilter: "blur(40px) saturate(180%)",
  border: `1px solid ${BORDER}`,
  borderRadius: 18,
  boxShadow: "0 8px 24px -14px rgba(39,92,115,0.18)",
  padding: 20,
  fontFamily: FONT,
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: TEXT3,
};

// ─── Mock data ──────────────────────────────────────────────────────────────
const OVERVIEW = [
  {
    key: "visitors",
    label: "Total Visitors",
    value: "24,318",
    trend: "+12.4%",
    up: true,
    positive: true,
    icon: <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />,
  },
  {
    key: "views",
    label: "Page Views",
    value: "86,742",
    trend: "+8.1%",
    up: true,
    positive: true,
    icon: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>,
  },
  {
    key: "bounce",
    label: "Bounce Rate",
    value: "42.3%",
    trend: "-3.2%",
    up: false,
    positive: true, // a falling bounce rate is good
    icon: <><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></>,
  },
  {
    key: "duration",
    label: "Avg Session Duration",
    value: "3m 24s",
    trend: "+5.6%",
    up: true,
    positive: true,
    icon: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  },
];

const TOP_PAGES = [
  { page: "/", views: "18,204", unique: "12,890", time: "2m 51s", bounce: "38.1%" },
  { page: "/pricing", views: "9,438", unique: "7,122", time: "3m 12s", bounce: "31.4%" },
  { page: "/features", views: "7,901", unique: "5,640", time: "2m 47s", bounce: "44.8%" },
  { page: "/blog/launch", views: "6,233", unique: "5,019", time: "4m 06s", bounce: "27.9%" },
  { page: "/contact", views: "4,512", unique: "3,880", time: "1m 58s", bounce: "52.3%" },
  { page: "/about", views: "3,977", unique: "3,201", time: "2m 19s", bounce: "47.6%" },
];

const SOURCES = [
  { label: "Direct", pct: 42, count: "10,213" },
  { label: "Organic Search", pct: 31, count: "7,538" },
  { label: "Social", pct: 18, count: "4,377" },
  { label: "Referral", pct: 9, count: "2,190" },
];

// Deterministic series so the chart is stable across re-renders.
function series(n, base, amp) {
  return Array.from({ length: n }, (_, i) =>
    Math.max(
      0,
      Math.round(
        base +
          amp * Math.sin(i / (n / 6)) +
          amp * 0.4 * Math.sin(i / 2.3 + 1) +
          i * amp * 0.012
      )
    )
  );
}

const RANGES = {
  "7d": { label: "7 days", data: [320, 412, 388, 520, 470, 610, 588], ticks: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
  "30d": { label: "30 days", data: series(30, 480, 140), ticks: ["Wk 1", "Wk 2", "Wk 3", "Wk 4"] },
  "90d": { label: "90 days", data: series(90, 520, 180), ticks: ["Month 1", "Month 2", "Month 3"] },
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function TrendBadge({ up, positive, children }) {
  const color = positive ? SUCCESS : DANGER;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 600, color }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: up ? "none" : "rotate(180deg)" }} aria-hidden="true">
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
      </svg>
      {children}
    </span>
  );
}

function OverviewCard({ item }) {
  return (
    <div style={{ ...glassCard, flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(39,92,115,0.10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
        </div>
        <TrendBadge up={item.up} positive={item.positive}>{item.trend}</TrendBadge>
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 700, color: TEXT, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{item.value}</div>
        <div style={{ ...labelStyle, marginTop: 6 }}>{item.label}</div>
      </div>
    </div>
  );
}

function TrafficChart({ data, ticks }) {
  // viewBox space; the SVG scales to its container width.
  const W = 720, H = 240, padX = 8, padTop = 16, padBottom = 28;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const n = data.length;

  const pts = data.map((v, i) => {
    const x = padX + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = padTop + innerH - ((v - min) / span) * innerH;
    return [x, y];
  });
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)} ${(padTop + innerH).toFixed(1)} L${pts[0][0].toFixed(1)} ${(padTop + innerH).toFixed(1)} Z`;
  const gridYs = [0, 0.25, 0.5, 0.75, 1].map((f) => padTop + innerH - f * innerH);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ display: "block" }} role="img" aria-label="Visitors over time">
      <defs>
        <linearGradient id="vellaTrafficFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={BLUE} stopOpacity="0.28" />
          <stop offset="100%" stopColor={BLUE} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {gridYs.map((y, i) => (
        <line key={i} x1={padX} y1={y} x2={W - padX} y2={y} stroke={BORDER} strokeWidth="1" />
      ))}
      <path d={areaPath} fill="url(#vellaTrafficFill)" />
      <path d={linePath} fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="4" fill={BLUE} stroke="#fff" strokeWidth="2" />
      {ticks.map((t, i) => (
        <text key={i} x={padX + (ticks.length === 1 ? innerW / 2 : (i / (ticks.length - 1)) * innerW)} y={H - 8} fontSize="11" fontFamily={FONT} fill={TEXT3} textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}>{t}</text>
      ))}
    </svg>
  );
}

export default function WebsiteAnalytics({ biz }) {
  const [range, setRange] = useState("7d");
  const current = RANGES[range];
  const peak = useMemo(() => Math.max(...current.data).toLocaleString(), [current]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, fontFamily: FONT }}>
      {/* 1 — Traffic overview cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {OVERVIEW.map((item) => <OverviewCard key={item.key} item={item} />)}
      </div>

      {/* 2 — Traffic chart */}
      <div style={glassCard}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Visitors over time</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, letterSpacing: "-0.01em", marginTop: 4 }}>
              Traffic — last {current.label}
            </div>
            <div style={{ fontSize: 12, color: TEXT3, marginTop: 2 }}>Peak {peak} visitors / day</div>
          </div>
          <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 10, background: "rgba(255,255,255,0.5)", border: `1px solid ${BORDER}` }}>
            {Object.keys(RANGES).map((k) => {
              const active = range === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setRange(k)}
                  style={{ border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8, color: active ? "#fff" : TEXT2, background: active ? BLUE : "transparent", transition: "background 160ms ease, color 160ms ease" }}
                >
                  {k.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>
        <VellaAreaChart
          data={current.data.map((v, i) => ({
            label: current.data.length === current.ticks.length ? current.ticks[i] : String(i + 1),
            visitors: v,
          }))}
          xKey="label"
          height={260}
          series={[{ key: "visitors", color: "#275c73", name: "Visitors" }]}
        />
      </div>

      {/* 3 + 4 — Top pages table + traffic sources */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }} className="vella-analytics-grid">
        {/* Top pages */}
        <div style={glassCard}>
          <div style={{ ...labelStyle, marginBottom: 14 }}>Top Pages</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["Page", "Views", "Unique Visitors", "Avg Time", "Bounce Rate"].map((h, i) => (
                    <th key={h} style={{ ...labelStyle, textAlign: i === 0 ? "left" : "right", padding: "8px 12px", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOP_PAGES.map((row, i) => (
                  <tr key={row.page} style={{ background: i % 2 === 1 ? "rgba(39,92,115,0.04)" : "transparent" }}>
                    <td style={{ padding: "10px 12px", color: BLUE, fontWeight: 600, whiteSpace: "nowrap" }}>{row.page}</td>
                    <td style={{ padding: "10px 12px", color: TEXT, textAlign: "right", whiteSpace: "nowrap" }}>{row.views}</td>
                    <td style={{ padding: "10px 12px", color: TEXT2, textAlign: "right", whiteSpace: "nowrap" }}>{row.unique}</td>
                    <td style={{ padding: "10px 12px", color: TEXT2, textAlign: "right", whiteSpace: "nowrap" }}>{row.time}</td>
                    <td style={{ padding: "10px 12px", color: TEXT2, textAlign: "right", whiteSpace: "nowrap" }}>{row.bounce}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Traffic sources */}
        <div style={glassCard}>
          <div style={{ ...labelStyle, marginBottom: 14 }}>Traffic Sources</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {SOURCES.map((s) => (
              <div key={s.label}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{s.label}</span>
                  <span style={{ fontSize: 12, color: TEXT3 }}>
                    <span style={{ color: TEXT2, fontWeight: 600 }}>{s.count}</span> · {s.pct}%
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: "rgba(39,92,115,0.08)", overflow: "hidden" }}>
                  <div style={{ width: `${s.pct}%`, height: "100%", borderRadius: 999, background: `linear-gradient(90deg, ${BLUE_MED}, ${BLUE})`, transition: "width 320ms cubic-bezier(0.22,1,0.36,1)" }} />
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: TEXT3, lineHeight: 1.5, margin: "18px 0 0" }}>
            Showing sample data for {biz?.website || biz?.name || "your site"}. Live analytics connect once your domain is verified.
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .vella-analytics-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
